import {
  BOX_CATEGORIES,
  BOX_CATEGORY_INFO,
  type BoxCategory,
  LIVE_BREAK_MAX_RATE_BPS,
} from "@loot-os/api-contract";
// Type-only import: the dashboard never bundles server code.
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

type HostsOutput = inferOutput<TRPCOptionsProxy<AppRouter>["liveBreaks"]["hosts"]["list"]>;

/** One row of `liveBreaks.hosts.list`: an end user with a host row in this environment. */
export type LiveBreakHost = HostsOutput["hosts"][number];

/** Basis points as a rate field shows them, a percent with two decimals: 150 is "1.50". */
export function ratePercentText(bps: number): string {
  return (bps / 100).toFixed(2);
}

/**
 * A rate field's text as basis points: a percent from 0 to `LIVE_BREAK_MAX_RATE_BPS` / 100, two
 * decimals at most. Null for anything else.
 */
export function parseRatePercent(text: string): number | null {
  // The text, not the float: 0.07 * 100 is 7.000000000000001.
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  const bps = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return bps <= LIVE_BREAK_MAX_RATE_BPS ? bps : null;
}

/**
 * The box categories a store leaves off, as the live-break settings name them: each family that is
 * off, and each game that is off while its family is on (a game under a family that is off is off
 * with it, so naming the family says it).
 */
export function categoriesLeftOff(offered: readonly BoxCategory[]): BoxCategory[] {
  const on = new Set(offered);
  return BOX_CATEGORIES.filter((code) => {
    if (on.has(code)) return false;
    const family = BOX_CATEGORY_INFO[code].parent;
    return family === null || on.has(family);
  });
}

/** "Allowed by" as the host list shows it: a tenant member's name, or platform staff. */
export function allowerLabel(allowedBy: LiveBreakHost["allowedBy"]): string {
  if (!allowedBy) return "";
  return allowedBy.kind === "member" ? allowedBy.name : "Platform staff";
}
